import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { 
  CheckCircle, 
  XCircle, 
  Clock,
  Calendar,
  User,
  Plane,
  Filter,
  Search,
  Ban,
  Info,
  Home,
  AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PTOManagement = () => {
  const { user, hasPermission } = useAuth();
  const [ptoRequests, setPtoRequests] = useState([]);
  const [wfhRequests, setWfhRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [ptoTypes, setPtoTypes] = useState([]); // Dynamic PTO types from backend
  const [activeTab, setActiveTab] = useState('pending');
  const [wfhActiveTab, setWfhActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterPTOType, setFilterPTOType] = useState('all');
  const [wfhSearchTerm, setWfhSearchTerm] = useState('');
  const [wfhFilterEmployee, setWfhFilterEmployee] = useState('all');
  const [cancelDialog, setCancelDialog] = useState({ open: false, type: null, id: null, status: null, reason: '', label: '' });
  const [approvePreview, setApprovePreview] = useState({ open: false, ptoId: null, request: null, days: [], isPartial: false });
  const [showAddPTOForm, setShowAddPTOForm] = useState(false);
  const [showEmployeeRequestForm, setShowEmployeeRequestForm] = useState(false);
  const [showWFHRequestForm, setShowWFHRequestForm] = useState(false);
  
  const [addPTOForm, setAddPTOForm] = useState({
    user_id: '',
    start_date: '',
    end_date: '',
    pto_type: 'pto',
    reason: '',
    auto_approve: true,
    is_partial_day: false,
    start_time: '09:00',
    end_time: '17:00',
    exclude_weekends: true
  });
  
  const [employeePTOForm, setEmployeePTOForm] = useState({
    start_date: '',
    end_date: '',
    pto_type: 'pto',
    reason: '',
    is_partial_day: false,
    start_time: '09:00',
    end_time: '17:00',
    exclude_weekends: true
  });
  
  const [wfhRequestForm, setWfhRequestForm] = useState({
    user_id: '',
    start_date: '',
    end_date: '',
    reason: '',
    wfh_type: 'flex_bank',
    hours: 8,
    wfh_period: 'morning'
  });
  
  const [wfhTypes, setWfhTypes] = useState([]);
  const [showBulkWFHModal, setShowBulkWFHModal] = useState(false);
  const [bulkWFHForm, setBulkWFHForm] = useState({
    date: '',
    reason: '',
    apply_to_all: true,
    selected_user_ids: []
  });
  const [bulkWFHLoading, setBulkWFHLoading] = useState(false);
  
  const [showEditWFHForm, setShowEditWFHForm] = useState(false);
  const [editingWFH, setEditingWFH] = useState(null);
  const [editWFHForm, setEditWFHForm] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  });
  
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [flexBankInfo, setFlexBankInfo] = useState(null);

  // Server-side pagination/counts state
  const [ptoCounts, setPtoCounts] = useState({ pending: 0, approved: 0, denied: 0, cancelled: 0 });
  const [wfhCounts, setWfhCounts] = useState({ pending: 0, approved: 0, denied: 0, cancelled: 0 });
  const [ptoLoadingMore, setPtoLoadingMore] = useState(false);
  const [ptoHasMore, setPtoHasMore] = useState(false);
  const [wfhLoadingMore, setWfhLoadingMore] = useState(false);
  const [wfhHasMore, setWfhHasMore] = useState(false);
  const PAGE_SIZE = 50;

  // Check if current user has management privileges
  const isManagerOrAdmin = user && (user.role === 'manager' || user.role === 'admin' || user.role === 'team_lead');

  // Build current-month date range string (YYYY-MM-DD)
  const getCurrentMonthRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: fmt(start), end: fmt(end) };
  };

  // Build query params for the active tab. Pending tab has no date filter; others narrow to current month
  const buildPtoParams = (tab, skip = 0) => {
    const params = new URLSearchParams();
    params.set('status', tab);
    params.set('limit', String(PAGE_SIZE));
    params.set('skip', String(skip));
    if (tab !== 'pending') {
      const { start, end } = getCurrentMonthRange();
      params.set('start_date_after', start);
      params.set('start_date_before', end);
    }
    // Employees only see their own records
    if (user && !isManagerOrAdmin) {
      params.set('employee_id', user.id || user.user_id);
    }
    return params.toString();
  };

  const buildWfhParams = (tab, skip = 0) => {
    const params = new URLSearchParams();
    params.set('status', tab);
    params.set('limit', String(PAGE_SIZE));
    params.set('skip', String(skip));
    if (tab !== 'pending') {
      const { start, end } = getCurrentMonthRange();
      params.set('start_date_after', start);
      params.set('start_date_before', end);
    }
    if (user && !isManagerOrAdmin) {
      params.set('user_id', user.id || user.user_id);
    }
    return params.toString();
  };

  const initDoneRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    fetchPtoCounts();
    fetchWfhCounts();
    fetchPTORequests(activeTab);
    fetchWFHRequests(wfhActiveTab);
    fetchUsers();
    fetchPTOTypes();
    fetchFlexBankInfo();
    fetchWFHTypes();
    initDoneRef.current = true;
  }, [user]);

  // Refetch PTO list only when the user actively changes tabs (not on initial mount)
  const lastPtoTabRef = useRef(activeTab);
  useEffect(() => {
    if (!user || !initDoneRef.current) return;
    if (lastPtoTabRef.current === activeTab) return;
    lastPtoTabRef.current = activeTab;
    fetchPTORequests(activeTab);
  }, [activeTab, user]);

  // Refetch WFH list only when the user actively changes tabs
  const lastWfhTabRef = useRef(wfhActiveTab);
  useEffect(() => {
    if (!user || !initDoneRef.current) return;
    if (lastWfhTabRef.current === wfhActiveTab) return;
    lastWfhTabRef.current = wfhActiveTab;
    fetchWFHRequests(wfhActiveTab);
  }, [wfhActiveTab, user]);

  const fetchPtoCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/pto-requests/counts`, { headers });
      setPtoCounts(response.data || {});
    } catch (error) {
      console.error('Failed to fetch PTO counts:', error);
    }
  };

  const fetchWfhCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/wfh-requests/counts`, { headers });
      setWfhCounts(response.data || {});
    } catch (error) {
      console.error('Failed to fetch WFH counts:', error);
    }
  };

  const fetchWFHTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/wfh-types`, { headers });
      setWfhTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch WFH types:', error);
      // Set default types if API fails
      setWfhTypes([
        { value: 'flex_bank', display_name: 'Flex Bank', description: 'Use your Flex Bank days', requires_balance: true, manager_only: false },
        { value: 'on_call', display_name: 'On-Call WFH', description: 'Work from home while on-call', requires_balance: false, manager_only: false },
        { value: 'business_directed', display_name: 'Business Directed', description: 'Company-directed remote work', requires_balance: false, manager_only: true }
      ]);
    }
  };

  const fetchPTORequests = async (tab = activeTab) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const qs = buildPtoParams(tab, 0);
      const response = await axios.get(`${API}/pto-requests?${qs}`, { headers });
      const data = response.data || [];
      setPtoRequests(data);
      setPtoHasMore(data.length >= PAGE_SIZE);
    } catch (error) {
      console.error('Failed to fetch PTO requests:', error);
      toast.error('Failed to load PTO requests');
    } finally {
      setLoading(false);
    }
  };

  const loadMorePtoRequests = async () => {
    try {
      setPtoLoadingMore(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const qs = buildPtoParams(activeTab, ptoRequests.length);
      const response = await axios.get(`${API}/pto-requests?${qs}`, { headers });
      const more = response.data || [];
      setPtoRequests((prev) => [...prev, ...more]);
      setPtoHasMore(more.length >= PAGE_SIZE);
    } catch (error) {
      console.error('Failed to load more PTO requests:', error);
      toast.error('Failed to load more PTO requests');
    } finally {
      setPtoLoadingMore(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/users`, { headers });
      // Filter out test users and inactive users
      const realUsers = (response.data || []).filter(u => {
        const email = u.email?.toLowerCase() || '';
        
        // Filter out inactive users
        if (u.is_active === false) return false;
        
        // Filter out specific test domains
        if (email.includes('@shiftela.com')) return false;
        if (email.includes('@example.com')) return false;
        
        // Filter out users that START with 'test' (testadmin, testuser, etc.)
        const emailPrefix = email.split('@')[0];
        if (emailPrefix.startsWith('test')) return false;
        
        return true;
      });
      setUsers(realUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchPTOTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/pto-types`, { headers });
      
      // Filter only active PTO types and format for dropdown — include id for UUID resolution
      const activePTOTypes = (response.data || [])
        .filter(type => type.is_active)
        .map(type => ({
          id: type.id,
          value: type.value,
          label: type.display_name,
          color: type.color
        }));
      
      setPtoTypes(activePTOTypes);
    } catch (error) {
      console.error('Failed to fetch PTO types:', error);
      // Set fallback options if fetch fails
      setPtoTypes([
        { value: 'pto', label: 'PTO' },
        { value: 'sick', label: 'Sick' }
      ]);
    }
  };

  const fetchWFHRequests = async (tab = wfhActiveTab) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      const qs = buildWfhParams(tab, 0);
      const response = await axios.get(`${API}/wfh-requests?${qs}`, { headers });
      const data = response.data || [];
      setWfhRequests(data);
      setWfhHasMore(data.length >= PAGE_SIZE);
    } catch (error) {
      console.error('Failed to fetch WFH requests:', error);
      toast.error('Failed to load WFH requests');
    }
  };

  const loadMoreWfhRequests = async () => {
    try {
      setWfhLoadingMore(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const qs = buildWfhParams(wfhActiveTab, wfhRequests.length);
      const response = await axios.get(`${API}/wfh-requests?${qs}`, { headers });
      const more = response.data || [];
      setWfhRequests((prev) => [...prev, ...more]);
      setWfhHasMore(more.length >= PAGE_SIZE);
    } catch (error) {
      console.error('Failed to load more WFH requests:', error);
      toast.error('Failed to load more WFH requests');
    } finally {
      setWfhLoadingMore(false);
    }
  };
  
  const fetchFlexBankInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const userId = user?.id || user?.user_id;
      
      if (!userId) return;
      
      const response = await axios.get(`${API}/users/${userId}/flex-bank`, { headers });
      setFlexBankInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch Flex Bank info:', error);
    }
  };
  
  const fetchUpcomingShifts = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${API}/users/${userId}/upcoming-shifts?days_ahead=60`, { headers });
      setUpcomingShifts(response.data?.shifts || []);
    } catch (error) {
      console.error('Failed to fetch upcoming shifts:', error);
      setUpcomingShifts([]);
    }
  };
  
  const openWFHRequestForm = async () => {
    // Use user.id directly from auth context
    const userId = user.id || user.user_id;
    if (userId) {
      await fetchUpcomingShifts(userId);
    }
    setShowWFHRequestForm(true);
  };

  const handleWFHSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Manager assigning to an employee vs self-request
      const isAssigning = isManagerOrAdmin && !!wfhRequestForm.user_id;
      const userId = isAssigning ? wfhRequestForm.user_id : (user.id || user.user_id);
      
      if (!userId) {
        toast.error('Unable to identify user. Please log out and log back in.');
        return;
      }
      
      const wfhType = wfhRequestForm.wfh_type || 'flex_bank';
      const hours = wfhRequestForm.hours || 8;
      const periodParam = hours === 4 ? `&wfh_period=${wfhRequestForm.wfh_period || 'morning'}` : '';
      const resp = await axios.post(`${API}/wfh-requests?start_date=${wfhRequestForm.start_date}&end_date=${wfhRequestForm.end_date}&user_id=${userId}&reason=${encodeURIComponent(wfhRequestForm.reason || '')}&wfh_type=${wfhType}&hours=${hours}${periodParam}`, {}, { headers });
      
      // When a manager assigns WFH to an employee, auto-approve so it applies to the schedule
      if (isAssigning && resp.data?.id) {
        await axios.put(`${API}/wfh-requests/${resp.data.id}/approve`, {}, { headers });
      }
      
      const typeLabel = wfhTypes.find(t => t.value === wfhType)?.display_name || wfhType;
      if (isAssigning) {
        const empName = users.find(u => u.id === userId)?.name || 'employee';
        toast.success(`${typeLabel} WFH assigned to ${empName} and applied to schedule`);
      } else {
        toast.success(`${typeLabel} WFH request submitted successfully`);
      }
      setShowWFHRequestForm(false);
      setWfhRequestForm({ user_id: '', start_date: '', end_date: '', reason: '', wfh_type: 'flex_bank', hours: 8, wfh_period: 'morning' });
      fetchWFHRequests(); fetchWfhCounts();
    } catch (error) {
      console.error('Failed to submit WFH request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit WFH request');
    }
  };

  const handleBulkWFHSubmit = async (e) => {
    e.preventDefault();
    setBulkWFHLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const payload = {
        target_date: bulkWFHForm.date,
        reason: bulkWFHForm.reason
      };
      
      if (!bulkWFHForm.apply_to_all && bulkWFHForm.selected_user_ids.length > 0) {
        payload.user_ids = bulkWFHForm.selected_user_ids;
      }
      
      const response = await axios.post(`${API}/wfh-requests/bulk-business-directed`, payload, { headers });
      
      toast.success(`Business Directed WFH created for ${response.data.total_created} employees`);
      if (response.data.total_skipped > 0) {
        toast.info(`${response.data.total_skipped} employees skipped (no shifts or already have WFH)`);
      }
      
      setShowBulkWFHModal(false);
      setBulkWFHForm({ date: '', reason: '', apply_to_all: true, selected_user_ids: [] });
      fetchWFHRequests(); fetchWfhCounts();
    } catch (error) {
      console.error('Failed to create bulk WFH:', error);
      toast.error(error.response?.data?.detail || 'Failed to create Business Directed WFH');
    } finally {
      setBulkWFHLoading(false);
    }
  };

  const handleWFHApprove = async (wfhId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.put(`${API}/wfh-requests/${wfhId}/approve`, {}, { headers });
      const { flex_bank_remaining, days_deducted, wfh_type, message, schedules_updated } = response.data;
      
      if (schedules_updated === 0) {
        toast.warning('WFH approved, but no matching regular office shift was found on those dates — the schedule was not changed. Check the shift exists and is an office shift.');
      } else if (wfh_type === 'flex_bank' && days_deducted > 0) {
        toast.success(`WFH request approved! ${days_deducted} Flex Bank day(s) deducted. ${flex_bank_remaining} remaining.`);
      } else {
        toast.success(message || 'WFH request approved!');
      }
      fetchWFHRequests(); fetchWfhCounts();
      fetchFlexBankInfo(); // Refresh Flex Bank balance
    } catch (error) {
      console.error('Failed to approve WFH request:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve WFH request');
    }
  };

  const handleWFHDeny = async (wfhId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(`${API}/wfh-requests/${wfhId}/deny`, {}, { headers });
      toast.success('WFH request denied');
      fetchWFHRequests(); fetchWfhCounts();
    } catch (error) {
      console.error('Failed to deny WFH request:', error);
      toast.error(error.response?.data?.detail || 'Failed to deny WFH request');
    }
  };

  const handleWFHDelete = async (wfhId) => {
    if (!window.confirm('Are you sure you want to delete this WFH request?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.delete(`${API}/wfh-requests/${wfhId}`, { headers });
      toast.success('WFH request deleted');
      fetchWFHRequests(); fetchWfhCounts();
    } catch (error) {
      console.error('Failed to delete WFH request:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete WFH request');
    }
  };

  const handleWFHCancel = (wfhId) => {
    setCancelDialog({ open: true, type: 'wfh', id: wfhId, status: 'approved', reason: '', label: 'Cancel WFH Request' });
  };

  const handleEditWFHClick = (request) => {
    setEditingWFH(request);
    setEditWFHForm({
      start_date: request.start_date,
      end_date: request.end_date,
      reason: request.reason || ''
    });
    setShowEditWFHForm(true);
  };

  const handleEditWFHSubmit = async () => {
    if (!editWFHForm.start_date || !editWFHForm.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(
        `${API}/wfh-requests/${editingWFH.id}?start_date=${editWFHForm.start_date}&end_date=${editWFHForm.end_date}&reason=${encodeURIComponent(editWFHForm.reason || '')}`,
        {},
        { headers }
      );
      
      toast.success('WFH request updated successfully');
      setShowEditWFHForm(false);
      setEditingWFH(null);
      fetchWFHRequests(); fetchWfhCounts();
    } catch (error) {
      console.error('Failed to update WFH request:', error);
      toast.error(error.response?.data?.detail || 'Failed to update WFH request');
    }
  };

  const doApprove = async (ptoId, request) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.put(`${API}/pto-requests/${ptoId}/approve?replace_shifts=true`, {}, { headers });
      const { schedules_created, schedules_replaced, schedules_modified } = response.data;
      if (schedules_replaced > 0 || schedules_modified > 0) {
        let message = 'PTO approved! ';
        if (schedules_replaced > 0) message += `${schedules_replaced} shift(s) replaced, `;
        if (schedules_modified > 0) message += `${schedules_modified} shift(s) adjusted, `;
        message += `${schedules_created} PTO schedule(s) created`;
        toast.success(message);
      } else {
        toast.success('PTO request approved and added to schedule');
      }
      fetchPTORequests(); fetchPtoCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve PTO request');
    }
  };

  const handleApprove = async (ptoId, request) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // Show a change preview only when the approval would actually modify/split existing shifts
      const res = await axios.get(`${API}/pto-requests/${ptoId}/approval-preview`, { headers });
      if (res.data?.will_modify_shifts) {
        setApprovePreview({ open: true, ptoId, request, days: res.data.days || [], isPartial: res.data.is_partial_day });
        return;
      }
      await doApprove(ptoId, request);
    } catch (error) {
      // If the preview fails for any reason, fall back to approving directly
      await doApprove(ptoId, request);
    }
  };

  const confirmApprove = async () => {
    const { ptoId, request } = approvePreview;
    setApprovePreview({ open: false, ptoId: null, request: null, days: [], isPartial: false });
    await doApprove(ptoId, request);
  };

  const handleDeny = async (ptoId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/pto-requests/${ptoId}/deny`, {}, { headers });
      toast.success('PTO request denied');
      fetchPTORequests(); fetchPtoCounts();
    } catch (error) {
      toast.error('Failed to deny PTO request');
    }
  };

  const handleCancelPTO = (ptoId, status) => {
    setCancelDialog({
      open: true, type: 'pto', id: ptoId, status, reason: '',
      label: status === 'approved' ? 'Cancel Approved PTO' : 'Cancel PTO Request'
    });
  };

  const confirmCancel = async () => {
    const { type, id, reason } = cancelDialog;
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const q = reason && reason.trim() ? `?reason=${encodeURIComponent(reason.trim())}` : '';
      if (type === 'pto') {
        const res = await axios.put(`${API}/pto-requests/${id}/cancel${q}`, {}, { headers });
        const restored = res.data?.schedules_restored;
        const removed = res.data?.schedules_removed;
        toast.success(restored > 0
          ? `PTO cancelled — original shift(s) restored`
          : (removed ? `PTO cancelled — ${removed} schedule entries removed` : 'PTO request cancelled'));
        fetchPTORequests(); fetchPtoCounts();
      } else {
        await axios.post(`${API}/wfh-requests/${id}/cancel${q}`, {}, { headers });
        toast.success('WFH request cancelled and shifts reverted to office');
        fetchWFHRequests(); fetchWfhCounts();
      }
      setCancelDialog({ open: false, type: null, id: null, status: null, reason: '', label: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    }
  };

  const handleAddPTO = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Create the PTO request
      const ptoResponse = await axios.post(`${API}/pto-requests`, addPTOForm, { headers });
      
      // Check if there are conflicts
      const hasConflicts = ptoResponse.data.has_conflicts;
      const conflicts = ptoResponse.data.conflicts || [];
      const conflictDates = conflicts.map(c => c.date).join(', ');

      if (ptoResponse.data.has_blackout) {
        const labels = (ptoResponse.data.blackout_conflicts || []).map(b => b.label).join(', ');
        toast.warning(`These dates fall in a blackout period${labels ? ` (${labels})` : ''}.`, { duration: 7000 });
      }

      if (hasConflicts) {
        toast.warning(`PTO request created with ${conflicts.length} scheduling conflict(s) on: ${conflictDates}. Manager can review and replace shifts when approving.`);
      }
      
      // Auto-approve if requested
      if (addPTOForm.auto_approve) {
        if (hasConflicts) {
          const confirmReplace = window.confirm(
            `This PTO has scheduling conflicts on: ${conflictDates}\n\n` +
            `${conflicts.length} existing shift(s) will be replaced with PTO.\n\n` +
            `Do you want to continue and replace the shifts?`
          );
          
          if (!confirmReplace) {
            toast.info('PTO request created but not approved. Resolve conflicts manually.');
            fetchPTORequests(); fetchPtoCounts();
            setShowAddPTOForm(false);
            resetAddPTOForm();
            return;
          }
        }
        
        await axios.put(`${API}/pto-requests/${ptoResponse.data.id}/approve?replace_shifts=true`, {}, { headers });
        
        if (hasConflicts) {
          toast.success(`PTO approved! ${conflicts.length} shift(s) replaced with PTO`);
        } else {
          toast.success('PTO added and approved successfully');
        }
      } else {
        if (!hasConflicts) {
          toast.success('PTO request created');
        }
      }
      
      fetchPTORequests(); fetchPtoCounts();
      setShowAddPTOForm(false);
      resetAddPTOForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add PTO');
    }
  };

  const handleEmployeeRequestPTO = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const ptoData = {
        ...employeePTOForm,
        user_id: user.id
      };
      
      const ptoRes = await axios.post(`${API}/pto-requests`, ptoData, { headers });
      if (ptoRes.data?.has_blackout) {
        const labels = (ptoRes.data.blackout_conflicts || []).map(b => b.label).join(', ');
        toast.warning(`Heads up: these dates fall in a blackout period${labels ? ` (${labels})` : ''}. Your request was submitted and flagged for manager review.`, { duration: 8000 });
      } else {
        toast.success('PTO request submitted for approval');
      }
      
      fetchPTORequests(); fetchPtoCounts();
      setShowEmployeeRequestForm(false);
      resetEmployeePTOForm();
    } catch (error) {
      toast.error('Failed to submit PTO request');
    }
  };

  const resetAddPTOForm = () => {
    setAddPTOForm({
      user_id: '',
      start_date: '',
      end_date: '',
      pto_type: 'pto',
      reason: '',
      auto_approve: true,
      is_partial_day: false,
      start_time: '09:00',
      end_time: '17:00',
      exclude_weekends: true
    });
  };

  const resetEmployeePTOForm = () => {
    setEmployeePTOForm({
      start_date: '',
      end_date: '',
      pto_type: 'pto',
      reason: '',
      is_partial_day: false,
      start_time: '09:00',
      end_time: '17:00',
      exclude_weekends: true
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Parse date string as local date to avoid timezone issues
    const [year, month, day] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getPTOTypeLabel = (type, request) => {
    if (!type) return 'Unknown';
    // Use backend-resolved display name if available
    if (request?.pto_type_display) return request.pto_type_display;
    // Check dynamic ptoTypes first — match by value OR id (UUID)
    const match = ptoTypes.find(t => t.value === type || t.id === type);
    if (match) return match.label;
    // Fallback: format the string nicely
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getPTOTypeColor = (type) => {
    const colors = {
      'pto': 'bg-blue-100 text-blue-800',
      'floating_holiday': 'bg-purple-100 text-purple-800',
      'company_holiday': 'bg-green-100 text-green-800',
      'sick': 'bg-red-100 text-red-800',
      'unplanned_absence': 'bg-orange-100 text-orange-800',
      'bereavement': 'bg-gray-100 text-gray-800',
      'jury_duty': 'bg-indigo-100 text-indigo-800',
      'military_leave': 'bg-teal-100 text-teal-800',
      'unpaid_leave': 'bg-yellow-100 text-yellow-800',
      'work_from_home': 'bg-cyan-100 text-cyan-800',
      'vacation': 'bg-blue-100 text-blue-800'
    };
    // For UUID-based types, resolve to value first via dynamic config
    const match = ptoTypes.find(t => t.value === type || t.id === type);
    const resolvedType = match ? match.value : type;
    return colors[resolvedType] || (match?.color ? match.color : 'bg-gray-100 text-gray-800');
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'denied': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Get user info for a PTO request
  const getUserForRequest = (request) => {
    // Backend already enriches PTO requests with employee data
    // Use that directly instead of local users array (which may filter out inactive users)
    if (request.employee && request.employee.name) {
      return request.employee;
    }
    
    // Fallback: try to find in local users array
    const localUser = users.find(u => u.id === request.user_id);
    if (localUser) {
      return localUser;
    }
    
    // Last resort: show deleted user message
    return { name: 'Deleted User', email: '(user no longer exists)' };
  };

  // Format submission timestamp
  const formatSubmissionTime = (timestamp) => {
    if (!timestamp) return '';
    // Timestamps are stored in UTC but may not have timezone suffix
    // Append 'Z' to indicate UTC if not already present
    let utcTimestamp = timestamp;
    if (typeof timestamp === 'string' && !timestamp.endsWith('Z') && !timestamp.includes('+')) {
      utcTimestamp = timestamp + 'Z';
    }
    const date = new Date(utcTimestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Filter PTO requests (status + date already filtered server-side; only apply client-only filters here)
  const getFilteredRequests = () => {
    let filtered = ptoRequests.filter(req => {
      // Privacy filter: only show own requests or all if manager/admin/team lead
      if (!isManagerOrAdmin && req.user_id !== user?.id) {
        return false;
      }

      // Filter out test users
      const requestUser = getUserForRequest(req);
      const email = requestUser.email?.toLowerCase() || '';

      // Filter out specific test domains
      if (email.includes('@shiftela.com') || email.includes('@example.com')) {
        return false;
      }

      // Filter out users that START with 'test' (testadmin, testuser, etc.)
      const emailPrefix = email.split('@')[0];
      if (emailPrefix.startsWith('test')) {
        return false;
      }

      // Search filter
      if (searchTerm && !requestUser.name?.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }

      // Employee filter
      if (filterEmployee !== 'all' && req.user_id !== filterEmployee) {
        return false;
      }

      // PTO type filter — also match UUID-based pto_type via config lookup
      if (filterPTOType !== 'all') {
        const matchType = ptoTypes.find(t => t.value === filterPTOType);
        const matchesFilter = req.pto_type === filterPTOType ||
          (matchType && req.pto_type === matchType.id);
        if (!matchesFilter) return false;
      }

      return true;
    });

    // Sort by date (newest first) — server already sorts but keep stable on Load More
    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  // Get my PTO requests (for employees)
  const getMyPTORequests = () => {
    if (!user) return [];
    return ptoRequests.filter(req => req.user_id === user.id)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  // Filtered WFH requests — mirrors PTO filtering (privacy + test users + search + employee)
  const getFilteredWfhRequests = () => {
    return wfhRequests.filter(req => {
      // Privacy filter: only show own requests unless manager/admin/team lead
      if (!isManagerOrAdmin && req.user_id !== user?.id) {
        return false;
      }

      // Filter out test users
      const requestUser = getUserForRequest(req);
      const email = requestUser.email?.toLowerCase() || '';
      if (email.includes('@shiftela.com') || email.includes('@example.com')) {
        return false;
      }
      const emailPrefix = email.split('@')[0];
      if (emailPrefix.startsWith('test')) {
        return false;
      }

      // Search filter (by employee name)
      if (wfhSearchTerm && !requestUser.name?.toLowerCase().includes(wfhSearchTerm.toLowerCase())) {
        return false;
      }

      // Employee filter
      if (wfhFilterEmployee !== 'all' && req.user_id !== wfhFilterEmployee) {
        return false;
      }

      return true;
    });
  };

  // Stat counts come from the server (full dataset, regardless of active tab)
  const pendingCount = ptoCounts.pending || 0;
  const approvedCount = ptoCounts.approved || 0;
  const deniedCount = ptoCounts.denied || 0;
  const cancelledCount = ptoCounts.cancelled || 0;

  const filteredRequests = getFilteredRequests();
  const myRequests = getMyPTORequests();

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">PTO Management</h1>
          <p className="text-gray-600 mt-1">
            {isManagerOrAdmin ? 'Review and manage team time-off requests' : 'View and submit your time-off requests'}
          </p>
        </div>
      </div>

      {isManagerOrAdmin && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Pending</p>
                    <p className="text-3xl font-bold text-gray-900">{pendingCount}</p>
                    <p className="text-xs text-gray-500 mt-1">Awaiting action</p>
                  </div>
                  <Clock className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Approved</p>
                    <p className="text-3xl font-bold text-gray-900">{approvedCount}</p>
                    <p className="text-xs text-gray-500 mt-1">Active & upcoming</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Denied</p>
                    <p className="text-3xl font-bold text-gray-900">{deniedCount}</p>
                    <p className="text-xs text-gray-500 mt-1">Not approved</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-gray-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Cancelled</p>
                    <p className="text-3xl font-bold text-gray-900">{cancelledCount}</p>
                    <p className="text-xs text-gray-500 mt-1">Removed from schedule</p>
                  </div>
                  <Ban className="h-8 w-8 text-gray-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Add PTO Button */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Add PTO for Employee</h3>
                  <p className="text-sm text-gray-600">Record past or upcoming PTO for team members</p>
                </div>
                <Button onClick={() => setShowAddPTOForm(!showAddPTOForm)}>
                  <Plane className="h-4 w-4 mr-2" />
                  Add PTO
                </Button>
              </div>

              {showAddPTOForm && (
                <form onSubmit={handleAddPTO} className="mt-6 space-y-4 border-t pt-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                      <Select
                        value={addPTOForm.user_id}
                        onValueChange={(value) => setAddPTOForm({...addPTOForm, user_id: value})}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select employee" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">PTO Type *</label>
                      <Select
                        value={addPTOForm.pto_type}
                        onValueChange={(value) => setAddPTOForm({...addPTOForm, pto_type: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ptoTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                      <Input
                        type="date"
                        value={addPTOForm.start_date}
                        onChange={(e) => setAddPTOForm({...addPTOForm, start_date: e.target.value})}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                      <Input
                        type="date"
                        value={addPTOForm.end_date}
                        onChange={(e) => setAddPTOForm({...addPTOForm, end_date: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                    <Input
                      type="text"
                      placeholder="Optional reason"
                      value={addPTOForm.reason}
                      onChange={(e) => setAddPTOForm({...addPTOForm, reason: e.target.value})}
                    />
                  </div>

                  {/* Partial Day Option */}
                  <div className="space-y-3 border-t pt-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="add_is_partial_day"
                        checked={addPTOForm.is_partial_day}
                        onChange={(e) => setAddPTOForm({...addPTOForm, is_partial_day: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="add_is_partial_day" className="text-sm font-medium">
                        Partial day (specify time range)
                      </label>
                    </div>

                    {addPTOForm.is_partial_day && (
                      <div className="grid grid-cols-2 gap-4 pl-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                          <Input
                            type="time"
                            value={addPTOForm.start_time}
                            onChange={(e) => setAddPTOForm({...addPTOForm, start_time: e.target.value})}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                          <Input
                            type="time"
                            value={addPTOForm.end_time}
                            onChange={(e) => setAddPTOForm({...addPTOForm, end_time: e.target.value})}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Exclude Weekends Option */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="add_exclude_weekends"
                      checked={addPTOForm.exclude_weekends}
                      onChange={(e) => setAddPTOForm({...addPTOForm, exclude_weekends: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="add_exclude_weekends" className="text-sm font-medium">
                      Exclude weekends (Saturday & Sunday)
                    </label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="auto_approve"
                      checked={addPTOForm.auto_approve}
                      onChange={(e) => setAddPTOForm({...addPTOForm, auto_approve: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="auto_approve" className="text-sm font-medium">
                      Auto-approve and add to schedule
                    </label>
                  </div>

                  <div className="flex space-x-3">
                    <Button type="submit">Add PTO</Button>
                    <Button type="button" variant="outline" onClick={() => setShowAddPTOForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Tabs and Filters */}
          <Card>
            <CardContent className="pt-6">
              {/* Tabs */}
              <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setActiveTab('pending')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'pending' 
                      ? 'bg-white text-gray-900 shadow' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Pending ({pendingCount})
                </button>
                <button
                  onClick={() => setActiveTab('approved')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'approved' 
                      ? 'bg-white text-gray-900 shadow' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Approved ({approvedCount})
                </button>
                <button
                  onClick={() => setActiveTab('denied')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'denied' 
                      ? 'bg-white text-gray-900 shadow' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Denied ({deniedCount})
                </button>
                <button
                  onClick={() => setActiveTab('cancelled')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'cancelled' 
                      ? 'bg-white text-gray-900 shadow' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Cancelled ({cancelledCount})
                </button>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by employee name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                  <SelectTrigger>
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="All Employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {users.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterPTOType} onValueChange={setFilterPTOType}>
                  <SelectTrigger>
                    <SelectValue placeholder="All PTO Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All PTO Types</SelectItem>
                    {ptoTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Request List */}
              <div className="space-y-3">
                {filteredRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <Plane className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No {activeTab} requests</h3>
                    <p className="text-gray-600">
                      {activeTab === 'pending' ? 'All caught up! No pending requests to review.' : `No ${activeTab} PTO requests found.`}
                    </p>
                  </div>
                ) : (
                  filteredRequests.map((request) => {
                    const requestUser = getUserForRequest(request);
                    const approver = request.approved_by ? users.find(u => u.id === request.approved_by) : null;
                    const canceller = request.cancelled_by ? users.find(u => u.id === request.cancelled_by) : null;
                    
                    return (
                      <div 
                        key={request.id}
                        className="p-4 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4 flex-1">
                            {/* User Avatar */}
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-semibold text-lg">
                                {requestUser.name?.charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>

                            {/* Request Details */}
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <h3 className="font-semibold text-gray-900">{requestUser.name || 'Deleted User'}</h3>
                                <Badge className={`text-xs ${getStatusColor(request.status)}`}>
                                  {request.status}
                                </Badge>
                              </div>
                              
                              <div className="space-y-1 text-sm text-gray-600">
                                <div className="flex items-center space-x-2">
                                  <Calendar className="h-4 w-4" />
                                  <span>{formatDate(request.start_date)} - {formatDate(request.end_date)}</span>
                                  {request.is_partial_day && (
                                    <Badge variant="outline" className="text-xs">
                                      {formatTime(request.start_time)} - {formatTime(request.end_time)}
                                    </Badge>
                                  )}
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  <Badge className={`text-xs ${getPTOTypeColor(request.pto_type)}`}>
                                    {getPTOTypeLabel(request.pto_type, request)}
                                  </Badge>
                                  {request.reason && (
                                    <span className="text-gray-500">• {request.reason}</span>
                                  )}
                                </div>
                                
                                {/* Submission timestamp */}
                                {request.created_at && (
                                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                                    <Clock className="h-3 w-3" />
                                    <span>Submitted {formatSubmissionTime(request.created_at)}</span>
                                  </div>
                                )}

                                {/* Show conflict warning */}
                                {request.conflicts && request.conflicts.length > 0 && request.status === 'pending' && (
                                  <div className="flex items-center space-x-2 text-orange-600 bg-orange-50 px-2 py-1 rounded mt-2">
                                    <AlertCircle className="h-4 w-4" />
                                    <span className="text-xs font-medium">
                                      {request.conflicts.length} scheduling conflict{request.conflicts.length > 1 ? 's' : ''} - will replace existing shifts
                                    </span>
                                  </div>
                                )}

                                {/* Show blackout warning */}
                                {request.blackout_conflicts && request.blackout_conflicts.length > 0 && request.status === 'pending' && (
                                  <div className="flex items-center space-x-2 text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded mt-2" data-testid={`blackout-warning-${request.id}`}>
                                    <AlertCircle className="h-4 w-4" />
                                    <span className="text-xs font-medium">
                                      Falls in blackout period: {request.blackout_conflicts.map(b => b.label).join(', ')}
                                    </span>
                                  </div>
                                )}

                                {(approver || canceller) && (
                                  <div className="flex items-center space-x-1 text-xs text-gray-500 mt-2">
                                    <Info className="h-3 w-3" />
                                    {request.status === 'approved' && approver && (
                                      <span>Approved by {approver.name || 'Manager'}</span>
                                    )}
                                    {request.status === 'denied' && approver && (
                                      <span>Denied by {approver.name || 'Manager'}</span>
                                    )}
                                    {request.status === 'cancelled' && canceller && (
                                      <span>Cancelled by {canceller.name || 'Manager'}</span>
                                    )}
                                  </div>
                                )}

                                {request.status === 'cancelled' && request.cancellation_reason && (
                                  <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1 mt-1" data-testid={`pto-cancel-reason-${request.id}`}>
                                    <span className="font-medium text-gray-700">Cancellation reason:</span> {request.cancellation_reason}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          {request.status === 'pending' && (
                            <div className="flex space-x-2 ml-4">
                              {/* Managers/Admins can approve/deny */}
                              {user && (user.role === 'admin' || user.role === 'manager') && (
                                <>
                                  <Button 
                                    size="sm" 
                                    onClick={() => handleApprove(request.id, request)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Approve
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="destructive"
                                    onClick={() => handleDeny(request.id)}
                                  >
                                    <XCircle className="h-4 w-4 mr-1" />
                                    Deny
                                  </Button>
                                </>
                              )}
                              {/* Employees can cancel their own PENDING requests */}
                              {user && request.user_id === user.id && request.status === 'pending' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleCancelPTO(request.id, request.status)}
                                  className="border-orange-500 text-orange-600 hover:bg-orange-50"
                                >
                                  <Ban className="h-4 w-4 mr-1" />
                                  Cancel Request
                                </Button>
                              )}
                            </div>
                          )}

                          {/* Cancel button for approved requests — requires cancel_approved_pto permission */}
                          {request.status === 'approved' && hasPermission('cancel_approved_pto') && (
                            <div className="flex space-x-2 ml-4">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleCancelPTO(request.id, request.status)}
                                className="border-red-400 text-red-600 hover:bg-red-50"
                                data-testid={`cancel-approved-${request.id}`}
                              >
                                <Ban className="h-4 w-4 mr-1" />
                                Cancel Approved PTO
                              </Button>
                            </div>
                          )}
                          
                          {/* Action Buttons for Denied Requests - Allow managers to reverse decision */}
                          {request.status === 'denied' && user && (user.role === 'admin' || user.role === 'manager') && (
                            <div className="flex space-x-2 ml-4">
                              <Button 
                                size="sm" 
                                onClick={() => handleApprove(request.id, request)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve Anyway
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                {ptoHasMore && filteredRequests.length > 0 && (
                  <div className="flex justify-center pt-4">
                    <Button
                      variant="outline"
                      onClick={loadMorePtoRequests}
                      disabled={ptoLoadingMore}
                      data-testid="pto-load-more-btn"
                    >
                      {ptoLoadingMore ? 'Loading...' : `Load more (showing ${filteredRequests.length} of ${ptoCounts[activeTab] || 0})`}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Employee Section - My PTO Requests */}
      {!isManagerOrAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>My PTO Requests</span>
              <Button onClick={() => setShowEmployeeRequestForm(!showEmployeeRequestForm)}>
                <Plane className="h-4 w-4 mr-2" />
                Request PTO
              </Button>
            </CardTitle>
            <CardDescription>View and submit your time-off requests</CardDescription>
          </CardHeader>
          <CardContent>
            {showEmployeeRequestForm && (
              <form onSubmit={handleEmployeeRequestPTO} className="mb-6 space-y-4 border-b pb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                    <Input
                      type="date"
                      value={employeePTOForm.start_date}
                      onChange={(e) => setEmployeePTOForm({...employeePTOForm, start_date: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                    <Input
                      type="date"
                      value={employeePTOForm.end_date}
                      onChange={(e) => setEmployeePTOForm({...employeePTOForm, end_date: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PTO Type *</label>
                  <Select
                    value={employeePTOForm.pto_type}
                    onValueChange={(value) => setEmployeePTOForm({...employeePTOForm, pto_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ptoTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                  <Input
                    type="text"
                    placeholder="Optional reason"
                    value={employeePTOForm.reason}
                    onChange={(e) => setEmployeePTOForm({...employeePTOForm, reason: e.target.value})}
                  />
                </div>

                {/* Partial Day Option */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="emp_is_partial_day"
                      checked={employeePTOForm.is_partial_day}
                      onChange={(e) => setEmployeePTOForm({...employeePTOForm, is_partial_day: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="emp_is_partial_day" className="text-sm font-medium">
                      Partial day (specify time range)
                    </label>
                  </div>

                  {employeePTOForm.is_partial_day && (
                    <div className="grid grid-cols-2 gap-4 pl-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                        <Input
                          type="time"
                          value={employeePTOForm.start_time}
                          onChange={(e) => setEmployeePTOForm({...employeePTOForm, start_time: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                        <Input
                          type="time"
                          value={employeePTOForm.end_time}
                          onChange={(e) => setEmployeePTOForm({...employeePTOForm, end_time: e.target.value})}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Exclude Weekends Option */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="emp_exclude_weekends"
                    checked={employeePTOForm.exclude_weekends}
                    onChange={(e) => setEmployeePTOForm({...employeePTOForm, exclude_weekends: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="emp_exclude_weekends" className="text-sm font-medium">
                    Exclude weekends (Saturday & Sunday)
                  </label>
                </div>

                <div className="flex space-x-3">
                  <Button type="submit">Submit Request</Button>
                  <Button type="button" variant="outline" onClick={() => setShowEmployeeRequestForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* My Requests List */}
            <div className="space-y-3">
              {myRequests.length === 0 ? (
                <div className="text-center py-12">
                  <Plane className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No PTO requests yet</h3>
                  <p className="text-gray-600">Submit your first time-off request to get started.</p>
                </div>
              ) : (
                myRequests.map((request) => (
                  <div 
                    key={request.id}
                    className="p-4 border border-gray-200 rounded-lg bg-white"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={`text-xs ${getStatusColor(request.status)}`}>
                        {request.status}
                      </Badge>
                      <Badge className={`text-xs ${getPTOTypeColor(request.pto_type)}`}>
                        {getPTOTypeLabel(request.pto_type, request)}
                      </Badge>
                    </div>
                    
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center space-x-2 text-gray-900 font-medium">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(request.start_date)} - {formatDate(request.end_date)}</span>
                        {request.is_partial_day && (
                          <Badge variant="outline" className="text-xs">
                            {formatTime(request.start_time)} - {formatTime(request.end_time)}
                          </Badge>
                        )}
                      </div>
                      
                      {request.reason && (
                        <p className="text-gray-600">{request.reason}</p>
                      )}

                      {request.status !== 'pending' && (
                        <p className="text-xs text-gray-500 mt-2">
                          {request.status === 'approved' && 'Approved and added to schedule'}
                          {request.status === 'denied' && 'Request was not approved'}
                          {request.status === 'cancelled' && 'Removed from schedule'}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add WFH for Employee (managers) */}
      {isManagerOrAdmin && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Add WFH for Employee</h3>
                <p className="text-sm text-gray-600">Assign a remote (work-from-home) day to a team member — full or half day</p>
              </div>
              <Button
                onClick={() => showWFHRequestForm ? setShowWFHRequestForm(false) : openWFHRequestForm()}
                data-testid="add-wfh-employee-btn"
              >
                <Home className="h-4 w-4 mr-2" />
                Add WFH
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* WFH Requests Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <Home className="h-5 w-5 text-blue-600" />
                <span>Work From Home Requests</span>
              </CardTitle>
              <CardDescription>Request to work remotely on scheduled days</CardDescription>
            </div>
            <div className="flex space-x-2">
              {isManagerOrAdmin && (
                <Button
                  onClick={() => setShowBulkWFHModal(true)}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="bulk-wfh-btn"
                >
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Business Directed WFH
                </Button>
              )}
              <Button
                onClick={() => showWFHRequestForm ? setShowWFHRequestForm(false) : openWFHRequestForm()}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="request-wfh-btn"
              >
                <Home className="h-4 w-4 mr-2" />
                Request WFH
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Bulk Business Directed WFH Modal */}
          {showBulkWFHModal && (
            <Card className="mb-6 border-purple-200 bg-purple-50">
              <CardHeader>
                <CardTitle className="text-lg text-purple-900">Business Directed WFH</CardTitle>
                <CardDescription className="text-purple-700">
                  Set all employees to work from home for a specific day (e.g., office closure, inclement weather)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleBulkWFHSubmit} className="space-y-4">
                  <div className="bg-purple-100 border border-purple-300 rounded-lg p-3">
                    <p className="text-sm text-purple-800">
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      This will auto-approve WFH for all employees with shifts on the selected date. No Flex Bank days will be deducted.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Date *</label>
                    <Input
                      type="date"
                      value={bulkWFHForm.date}
                      onChange={(e) => setBulkWFHForm({...bulkWFHForm, date: e.target.value})}
                      required
                      className="bg-white"
                      data-testid="bulk-wfh-date"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Reason *</label>
                    <Input
                      type="text"
                      placeholder="Office closure, inclement weather, building maintenance..."
                      value={bulkWFHForm.reason}
                      onChange={(e) => setBulkWFHForm({...bulkWFHForm, reason: e.target.value})}
                      required
                      className="bg-white"
                      data-testid="bulk-wfh-reason"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="apply_to_all"
                      checked={bulkWFHForm.apply_to_all}
                      onChange={(e) => setBulkWFHForm({...bulkWFHForm, apply_to_all: e.target.checked})}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="apply_to_all" className="text-sm font-medium">
                      Apply to all employees with shifts on this date
                    </label>
                  </div>
                  
                  {!bulkWFHForm.apply_to_all && (
                    <div>
                      <label className="block text-sm font-medium mb-2">Select Employees</label>
                      <div className="max-h-48 overflow-y-auto border rounded-lg p-2 bg-white space-y-1">
                        {users.map(u => (
                          <label key={u.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded">
                            <input
                              type="checkbox"
                              checked={bulkWFHForm.selected_user_ids.includes(u.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setBulkWFHForm({...bulkWFHForm, selected_user_ids: [...bulkWFHForm.selected_user_ids, u.id]});
                                } else {
                                  setBulkWFHForm({...bulkWFHForm, selected_user_ids: bulkWFHForm.selected_user_ids.filter(id => id !== u.id)});
                                }
                              }}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm">{u.name || u.username}</span>
                            <Badge variant="outline" className="text-xs">{u.role}</Badge>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {bulkWFHForm.selected_user_ids.length} employee(s) selected
                      </p>
                    </div>
                  )}
                  
                  <div className="flex space-x-2">
                    <Button 
                      type="submit" 
                      className="bg-purple-600 hover:bg-purple-700"
                      disabled={bulkWFHLoading}
                      data-testid="bulk-wfh-submit"
                    >
                      {bulkWFHLoading ? 'Creating...' : 'Create Business Directed WFH'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowBulkWFHModal(false);
                        setBulkWFHForm({ date: '', reason: '', apply_to_all: true, selected_user_ids: [] });
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* WFH Request Form */}
          {showWFHRequestForm && (
            <Card className="mb-6 border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-lg">Request Work From Home</CardTitle>
                <CardDescription>
                  Request to work remotely on scheduled days
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleWFHSubmit} className="space-y-4">
                  {/* Employee selector - managers can assign WFH to a team member */}
                  {isManagerOrAdmin && (
                    <div>
                      <label className="block text-sm font-medium mb-2">Employee</label>
                      <Select
                        value={wfhRequestForm.user_id || 'self'}
                        onValueChange={(value) => {
                          const uid = value === 'self' ? '' : value;
                          setWfhRequestForm({ ...wfhRequestForm, user_id: uid });
                          fetchUpcomingShifts(uid || (user.id || user.user_id));
                        }}
                      >
                        <SelectTrigger className="bg-white" data-testid="wfh-assign-employee-select">
                          <SelectValue placeholder="Myself" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="self">Myself</SelectItem>
                          {users
                            .slice()
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map(u => (
                              <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {wfhRequestForm.user_id && (
                        <p className="text-xs text-teal-600 mt-1">Assigning for this employee — it will be auto-approved and applied to the schedule.</p>
                      )}
                    </div>
                  )}

                  {/* WFH Type Selector */}
                  <div>
                    <label className="block text-sm font-medium mb-2">WFH Type *</label>
                    <Select
                      value={wfhRequestForm.wfh_type}
                      onValueChange={(value) => setWfhRequestForm({...wfhRequestForm, wfh_type: value})}
                    >
                      <SelectTrigger className="bg-white" data-testid="wfh-type-select">
                        <SelectValue placeholder="Select WFH type" />
                      </SelectTrigger>
                      <SelectContent>
                        {wfhTypes
                          .filter(type => type.value !== 'business_directed')
                          .map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex flex-col">
                                <span>{type.display_name}</span>
                                <span className="text-xs text-gray-500">{type.description}</span>
                              </div>
                            </SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                    {wfhRequestForm.wfh_type !== 'flex_bank' && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ This type does not deduct from Flex Bank balance
                      </p>
                    )}
                  </div>

                  {/* Flex Bank Balance Display - only show for flex_bank type */}
                  {wfhRequestForm.wfh_type === 'flex_bank' && flexBankInfo && (
                    <div className={`rounded-lg p-4 border ${
                      flexBankInfo.flex_bank_balance > 5 
                        ? 'bg-green-50 border-green-200' 
                        : flexBankInfo.flex_bank_balance > 0 
                          ? 'bg-yellow-50 border-yellow-200' 
                          : 'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-700">Your Flex Bank Balance</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {flexBankInfo.flex_bank_balance} <span className="text-sm font-normal">days remaining</span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Year {flexBankInfo.flex_bank_year}</p>
                          <p className="text-xs text-gray-500">Used: {flexBankInfo.days_used} of {flexBankInfo.default_days}</p>
                        </div>
                      </div>
                      {flexBankInfo.flex_bank_balance === 0 && flexBankInfo.auto_reject_enabled && (
                        <p className="text-xs text-red-600 mt-2">
                          ⚠️ You have no Flex Bank days remaining. New requests will be automatically rejected.
                        </p>
                      )}
                      {flexBankInfo.flex_bank_balance === 0 && !flexBankInfo.auto_reject_enabled && (
                        <p className="text-xs text-amber-600 mt-2">
                          ⚠️ You have no Flex Bank days remaining. Manager approval required for exceptions.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Upcoming Shifts Helper */}
                  {upcomingShifts.length > 0 && (
                    <div className="bg-blue-100 border border-blue-300 rounded-lg p-3">
                      <p className="text-sm font-semibold text-blue-900 mb-2">📅 Your Upcoming Scheduled Shifts (Next 60 Days):</p>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {upcomingShifts.map((shift, idx) => {
                          // Parse date string as local date to avoid timezone issues
                          const [year, month, day] = shift.date.split('-');
                          const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                          
                          return (
                            <div key={shift.id || `${shift.date}-${shift.start_time}-${idx}`} className="text-xs text-blue-800 bg-white rounded px-2 py-1">
                              {localDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                              {' - '}
                              {shift.start_time} to {shift.end_time}
                              {shift.work_location && ` (${shift.work_location})`}
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-blue-700 mt-2 italic">
                        💡 You can only request WFH for dates when you have scheduled shifts
                      </p>
                    </div>
                  )}
                  {upcomingShifts.length === 0 && (
                    <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        ⚠️ No upcoming shifts found. You need to have scheduled shifts to request work from home.
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Start Date *</label>
                      <Input
                        type="date"
                        value={wfhRequestForm.start_date}
                        onChange={(e) => setWfhRequestForm({...wfhRequestForm, start_date: e.target.value})}
                        required
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">End Date *</label>
                      <Input
                        type="date"
                        value={wfhRequestForm.end_date}
                        onChange={(e) => setWfhRequestForm({...wfhRequestForm, end_date: e.target.value})}
                        required
                        className="bg-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Reason (Optional)</label>
                    <Input
                      type="text"
                      placeholder="Doctor appointment, personal matter, etc..."
                      value={wfhRequestForm.reason}
                      onChange={(e) => setWfhRequestForm({...wfhRequestForm, reason: e.target.value})}
                      className="bg-white"
                    />
                  </div>

                  {/* Duration: full or half day */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Duration</label>
                    <div className="flex space-x-3">
                      {[{ v: 8, l: 'Full day' }, { v: 4, l: 'Half day' }].map(opt => (
                        <label
                          key={opt.v}
                          className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 border rounded-lg cursor-pointer bg-white transition-colors ${
                            wfhRequestForm.hours === opt.v ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:bg-gray-50'
                          }`}
                          data-testid={`wfh-duration-${opt.v === 4 ? 'half' : 'full'}`}
                        >
                          <input type="radio" name="wfh-duration" checked={wfhRequestForm.hours === opt.v}
                            onChange={() => setWfhRequestForm({ ...wfhRequestForm, hours: opt.v })} className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium">{opt.l}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Which half (only for half-day) */}
                  {wfhRequestForm.hours === 4 && (
                    <div>
                      <label className="block text-sm font-medium mb-2">Which part of the shift?</label>
                      <div className="flex space-x-3">
                        {[{ v: 'morning', l: 'Morning', d: 'First half remote' }, { v: 'afternoon', l: 'Afternoon', d: 'Second half remote' }].map(opt => (
                          <label
                            key={opt.v}
                            className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 border rounded-lg cursor-pointer bg-white transition-colors ${
                              (wfhRequestForm.wfh_period || 'morning') === opt.v ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:bg-gray-50'
                            }`}
                            data-testid={`wfh-period-${opt.v}`}
                          >
                            <input type="radio" name="wfh-req-period" checked={(wfhRequestForm.wfh_period || 'morning') === opt.v}
                              onChange={() => setWfhRequestForm({ ...wfhRequestForm, wfh_period: opt.v })} className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium">{opt.l}</span>
                            <span className="text-xs text-gray-500 hidden sm:inline">({opt.d})</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex space-x-2">
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="wfh-request-submit">
                      {isManagerOrAdmin && wfhRequestForm.user_id ? 'Assign WFH' : 'Submit Request'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowWFHRequestForm(false);
                        setWfhRequestForm({ user_id: '', start_date: '', end_date: '', reason: '', wfh_type: 'flex_bank', hours: 8, wfh_period: 'morning' });
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Edit WFH Request Form */}
          {showEditWFHForm && editingWFH && (
            <Card className="mb-6 border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-lg">Edit WFH Request</CardTitle>
                <CardDescription>Update dates or reason for your pending WFH request</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => { e.preventDefault(); handleEditWFHSubmit(); }} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Start Date *</label>
                      <Input
                        type="date"
                        value={editWFHForm.start_date}
                        onChange={(e) => setEditWFHForm({...editWFHForm, start_date: e.target.value})}
                        required
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">End Date *</label>
                      <Input
                        type="date"
                        value={editWFHForm.end_date}
                        onChange={(e) => setEditWFHForm({...editWFHForm, end_date: e.target.value})}
                        required
                        className="bg-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Reason (Optional)</label>
                    <Input
                      value={editWFHForm.reason}
                      onChange={(e) => setEditWFHForm({...editWFHForm, reason: e.target.value})}
                      placeholder="Update reason for WFH request..."
                      className="bg-white"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      type="submit"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Update Request
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowEditWFHForm(false);
                        setEditingWFH(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* WFH Status Tabs */}
          <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setWfhActiveTab('pending')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                wfhActiveTab === 'pending' 
                  ? 'bg-white text-gray-900 shadow' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Pending ({wfhCounts.pending || 0})
            </button>
            <button
              onClick={() => setWfhActiveTab('approved')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                wfhActiveTab === 'approved' 
                  ? 'bg-white text-gray-900 shadow' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Approved ({wfhCounts.approved || 0})
            </button>
            <button
              onClick={() => setWfhActiveTab('denied')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                wfhActiveTab === 'denied' 
                  ? 'bg-white text-gray-900 shadow' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Denied ({wfhCounts.denied || 0})
            </button>
            <button
              onClick={() => setWfhActiveTab('cancelled')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                wfhActiveTab === 'cancelled' 
                  ? 'bg-white text-gray-900 shadow' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Cancelled ({wfhCounts.cancelled || 0})
            </button>
          </div>

          {/* WFH Filters (search + employee) — mirrors PTO */}
          {isManagerOrAdmin && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by employee name"
                  value={wfhSearchTerm}
                  onChange={(e) => setWfhSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="wfh-search-input"
                />
              </div>

              <Select value={wfhFilterEmployee} onValueChange={setWfhFilterEmployee}>
                <SelectTrigger data-testid="wfh-employee-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {users.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* WFH Requests List (status & current-month filters now done server-side) */}
          <div className="space-y-4">
            {getFilteredWfhRequests().length === 0 ? (
              <div className="text-center py-12">
                <Home className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No {wfhActiveTab} WFH requests</h3>
                <p className="text-gray-600">
                  {wfhActiveTab === 'pending' ? 'No pending work from home requests' : `No ${wfhActiveTab} WFH requests found.`}
                </p>
              </div>
            ) : (
              getFilteredWfhRequests()
                .map((request) => {
                  const requestUser = getUserForRequest(request);
                  
                  // Parse dates as local to avoid timezone issues
                  const [startYear, startMonth, startDay] = request.start_date.split('-');
                  const startDate = new Date(parseInt(startYear), parseInt(startMonth) - 1, parseInt(startDay));
                  
                  const [endYear, endMonth, endDay] = request.end_date.split('-');
                  const endDate = new Date(parseInt(endYear), parseInt(endMonth) - 1, parseInt(endDay));
                  
                  // Use stored days_requested for accurate partial day support
                  // Fall back to date range calculation only for legacy requests
                  let days = request.days_requested;
                  if (days === undefined || days === null) {
                    // Legacy calculation for old requests without days_requested
                    days = 0;
                    const currentDate = new Date(startDate);
                    while (currentDate <= endDate) {
                      const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
                      if (request.exclude_weekends && (dayOfWeek === 0 || dayOfWeek === 6)) {
                        // Skip weekends
                      } else {
                        days++;
                      }
                      currentDate.setDate(currentDate.getDate() + 1);
                    }
                  }
                  
                  // Get WFH type display info
                  const wfhType = request.wfh_type || 'flex_bank';
                  const wfhTypeInfo = wfhTypes.find(t => t.value === wfhType || t.id === wfhType) || { display_name: wfhType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) };
                  const wfhTypeBadgeColor = wfhType === 'flex_bank' ? 'bg-blue-100 text-blue-800' :
                                            wfhType === 'on_call' ? 'bg-orange-100 text-orange-800' :
                                            wfhType === 'business_directed' ? 'bg-purple-100 text-purple-800' :
                                            'bg-gray-100 text-gray-800';
                  
                  return (
                    <Card key={request.id} className={`border-l-4 ${
                      wfhType === 'business_directed' ? 'border-l-purple-500' :
                      wfhType === 'on_call' ? 'border-l-orange-500' :
                      'border-l-blue-500'
                    }`}>
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                wfhType === 'business_directed' ? 'bg-purple-100' :
                                wfhType === 'on_call' ? 'bg-orange-100' :
                                'bg-blue-100'
                              }`}>
                                <User className={`h-5 w-5 ${
                                  wfhType === 'business_directed' ? 'text-purple-600' :
                                  wfhType === 'on_call' ? 'text-orange-600' :
                                  'text-blue-600'
                                }`} />
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900">{requestUser.name || 'Deleted User'}</h3>
                                <p className="text-sm text-gray-600">{requestUser.email}</p>
                              </div>
                              <Badge className={wfhTypeBadgeColor} data-testid="wfh-type-badge">
                                {wfhTypeInfo.display_name}
                              </Badge>
                              <Badge className={
                                request.status === 'approved' ? 'bg-green-100 text-green-800' :
                                request.status === 'denied' ? 'bg-red-100 text-red-800' :
                                request.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                'bg-yellow-100 text-yellow-800'
                              }>
                                {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                              </Badge>
                            </div>

                            <div className="space-y-2 text-sm">
                              <div className="flex items-center space-x-2">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <span className="font-medium">
                                  {request.selected_dates && request.selected_dates.length > 0
                                    ? request.selected_dates.map(d => {
                                        const [y, m, dd] = d.split('-');
                                        return new Date(parseInt(y), parseInt(m) - 1, parseInt(dd)).toLocaleDateString();
                                      }).join(', ')
                                    : `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                                  }
                                </span>
                                <span className="text-gray-500">
                                  ({days} day{days !== 1 ? 's' : ''}{request.exclude_weekends ? ', weekdays only' : ''})
                                </span>
                              </div>
                              {wfhType !== 'flex_bank' && (
                                <div className="flex items-center space-x-1 text-xs text-green-600">
                                  <CheckCircle className="h-3 w-3" />
                                  <span>No Flex Bank deduction</span>
                                </div>
                              )}
                              {request.reason && (
                                <div className="flex items-start space-x-2">
                                  <Info className="h-4 w-4 text-gray-400 mt-0.5" />
                                  <span className="text-gray-600">{request.reason}</span>
                                </div>
                              )}
                              {/* Submission timestamp */}
                              {request.created_at && (
                                <div className="flex items-center space-x-1 text-xs text-gray-500">
                                  <Clock className="h-3 w-3" />
                                  <span>Submitted {formatSubmissionTime(request.created_at)}</span>
                                </div>
                              )}
                              {request.status === 'cancelled' && (request.cancelled_by_name || request.cancellation_reason) && (
                                <div className="mt-1 space-y-1">
                                  {request.cancelled_by_name && (
                                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                                      <Info className="h-3 w-3" />
                                      <span>Cancelled by {request.cancelled_by_name}</span>
                                    </div>
                                  )}
                                  {request.cancellation_reason && (
                                    <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1" data-testid={`wfh-cancel-reason-${request.id}`}>
                                      <span className="font-medium text-gray-700">Cancellation reason:</span> {request.cancellation_reason}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Manager actions for pending requests */}
                          {isManagerOrAdmin && request.status === 'pending' && (
                            <div className="flex space-x-2 ml-4">
                              <Button
                                size="sm"
                                onClick={() => handleWFHApprove(request.id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleWFHDeny(request.id)}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Deny
                              </Button>
                            </div>
                          )}
                          
                          {/* User actions for pending requests */}
                          {!isManagerOrAdmin && request.status === 'pending' && (
                            <div className="flex space-x-2 ml-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditWFHClick(request)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleWFHDelete(request.id)}
                              >
                                Delete
                              </Button>
                            </div>
                          )}
                          
                          {/* Cancel button for approved WFH — requires cancel_approved_wfh permission */}
                          {request.status === 'approved' && hasPermission('cancel_approved_wfh') && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleWFHCancel(request.id)}
                              className="ml-4"
                              data-testid={`wfh-cancel-approved-${request.id}`}
                            >
                              <Ban className="h-4 w-4 mr-1" />
                              Cancel
                            </Button>
                          )}
                          
                          {/* Approve button for denied requests - Allow managers to reverse decision */}
                          {isManagerOrAdmin && request.status === 'denied' && (
                            <div className="flex space-x-2 ml-4">
                              <Button
                                size="sm"
                                onClick={() => handleWFHApprove(request.id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve Anyway
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
            )}
            {wfhHasMore && wfhRequests.length > 0 && (
              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  onClick={loadMoreWfhRequests}
                  disabled={wfhLoadingMore}
                  data-testid="wfh-load-more-btn"
                >
                  {wfhLoadingMore ? 'Loading...' : `Load more (showing ${wfhRequests.length} of ${wfhCounts[wfhActiveTab] || 0})`}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cancellation reason dialog (PTO & WFH) */}
      <Dialog open={cancelDialog.open} onOpenChange={(o) => !o && setCancelDialog({ open: false, type: null, id: null, status: null, reason: '', label: '' })}>
        <DialogContent data-testid="cancel-reason-dialog">
          <DialogHeader>
            <DialogTitle>{cancelDialog.label || 'Cancel Request'}</DialogTitle>
            <DialogDescription>
              {cancelDialog.type === 'wfh'
                ? 'Shifts will be reverted back to office. Add an optional reason the employee will see.'
                : cancelDialog.status === 'approved'
                  ? 'PTO entries will be removed and the original shift restored. Add an optional reason the employee will see.'
                  : 'This request will be cancelled. Add an optional reason the employee will see.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="block text-sm font-medium mb-2">Reason (optional)</label>
            <Textarea
              value={cancelDialog.reason}
              onChange={(e) => setCancelDialog({ ...cancelDialog, reason: e.target.value })}
              placeholder="e.g. Coverage no longer needed, requested by employee, scheduling change..."
              rows={3}
              data-testid="cancel-reason-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialog({ open: false, type: null, id: null, status: null, reason: '', label: '' })}
              data-testid="cancel-reason-dismiss"
            >
              Keep Request
            </Button>
            <Button
              variant="destructive"
              onClick={confirmCancel}
              data-testid="cancel-reason-confirm"
            >
              <Ban className="h-4 w-4 mr-1" />
              Confirm Cancellation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change preview dialog before approving a PTO that modifies shifts */}
      <Dialog open={approvePreview.open} onOpenChange={(o) => !o && setApprovePreview({ open: false, ptoId: null, request: null, days: [], isPartial: false })}>
        <DialogContent className="max-w-lg" data-testid="approve-preview-dialog">
          <DialogHeader>
            <DialogTitle>Review shift changes</DialogTitle>
            <DialogDescription>
              Approving this PTO will change the schedule as shown below. Please confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="py-1 max-h-80 overflow-y-auto space-y-3" data-testid="approve-preview-list">
            {approvePreview.days.map((day) => (
              <div key={day.date} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="font-medium text-sm text-gray-900">{formatDate(day.date)}</span>
                  <Badge variant="outline" className="text-xs">PTO {day.pto_block}</Badge>
                </div>
                {day.changes.length === 0 ? (
                  <p className="text-xs text-gray-500">No existing shift — PTO will be added.</p>
                ) : (
                  <div className="space-y-1">
                    {day.changes.map((c, i) => (
                      <div key={i} className="flex items-center flex-wrap gap-2 text-xs">
                        {c.action === 'remove' && (
                          <>
                            <span className="line-through text-gray-400">{c.before}</span>
                            <span className="text-rose-600 font-medium">removed (full PTO)</span>
                          </>
                        )}
                        {c.action === 'trim_start' && (
                          <><span className="text-gray-500">{c.before}</span><span className="text-gray-400">→</span><span className="text-blue-700 font-medium">{c.after}</span></>
                        )}
                        {c.action === 'trim_end' && (
                          <><span className="text-gray-500">{c.before}</span><span className="text-gray-400">→</span><span className="text-blue-700 font-medium">{c.after}</span></>
                        )}
                        {c.action === 'split' && (
                          <><span className="text-gray-500">{c.before}</span><span className="text-gray-400">→</span><span className="text-blue-700 font-medium">{c.after}</span><span className="text-gray-400">+</span><span className="text-blue-700 font-medium">{c.extra}</span></>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setApprovePreview({ open: false, ptoId: null, request: null, days: [], isPartial: false })}
              data-testid="approve-preview-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={confirmApprove}
              data-testid="approve-preview-confirm"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Approve &amp; Apply Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PTOManagement;
